import { useState, useEffect, useContext } from 'react';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { LuCircleEllipsis } from 'react-icons/lu';
import InsuranceInfoDrawer from '../../CampaignDetail/drawers/InsuranceInfoDrawer';
import { CampaignContext } from '~/context/CampaignProvider';

interface Props {
    campaignRequest: any;
    patient: any;
    metadata: any;
    application: any;
    campaignApproval?: any; // Campaign approval by HHL staff (NOT used currently)
    onCampaignRequestUpdate?: (updatedCampaignRequest: any) => void;
    completionStatus?: any;
    onDataSaved?: () => void;
}

const InsuranceInfoTab: React.FC<Props> = ({
    campaignRequest,
    patient,
    metadata,
    application,
    campaignApproval,
    onCampaignRequestUpdate,
    completionStatus,
    onDataSaved,
}) => {
    const contextValue = useContext(CampaignContext);
    if (!contextValue) throw new Error('CampaignContext not found');

    const [state, campaignDispatch] = contextValue as unknown as [
        { campaignData: any },
        any
    ];
    const campaignData = state.campaignData;

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [localApplication, setLocalApplication] = useState(application);

    // Update local application when prop changes
    useEffect(() => {
        setLocalApplication(application);
    }, [application]);

    // Check completion status for insurance section
    const insuranceSection = completionStatus?.sections?.find(
        (s: any) => s.key === 'insurance'
    );
    const isInsuranceComplete = insuranceSection?.is_complete ?? false;

    // Whether the "Do you have insurance?" question has been answered at all.
    // A blank application has no flags/fields set, so show "Not provided"
    // rather than defaulting the display to "Yes".
    const insuranceAnswered =
        localApplication?.ins_has_none === true ||
        !!localApplication?.ins_has_medicare ||
        !!localApplication?.ins_has_medicaid ||
        !!localApplication?.ins_has_private ||
        !!localApplication?.ins_has_cobra ||
        !!localApplication?.ins_unknown ||
        !!localApplication?.ins_company ||
        !!localApplication?.ins_comments ||
        !!localApplication?.ins_premium;

    return (
        <>
            <div className='mb-7.5'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h3
                            className={`mb-2 text-[16px]/[24px] font-medium uppercase ${
                                isInsuranceComplete
                                    ? 'text-[#AFC12D]'
                                    : 'text-[#FEBF3E]'
                            }`}
                        >
                            {isInsuranceComplete ? (
                                <MdOutlineCheckCircle
                                    className='me-2'
                                    size={24}
                                />
                            ) : (
                                <LuCircleEllipsis className='me-2' size={24} />
                            )}
                            {isInsuranceComplete ? 'Complete' : 'Pending'}
                        </h3>
                        <h2 className='text-[22px]/[28px] font-bold'>
                            Insurance Info
                        </h2>
                    </div>
                    <button
                        className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsDrawerOpen(true)}
                    >
                        Edit
                    </button>
                </div>

                <div className='flex justify-between'>
                    <div className=''>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Do you have Insurance?
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <div>
                                {!insuranceAnswered
                                    ? 'Not provided'
                                    : localApplication?.ins_has_none
                                      ? 'No'
                                      : 'Yes'}
                            </div>
                        </div>
                        {insuranceAnswered && !localApplication?.ins_has_none && (
                            <div className='border-l-2 border-[#E9E9E9] pl-[16px]'>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Check all that apply:
                                        <span className='align-top text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    <div>
                                        <div>
                                            {localApplication?.ins_has_medicare
                                                ? `I have Medicare`
                                                : ''}
                                        </div>
                                        <div>
                                            {localApplication?.ins_has_medicaid
                                                ? 'I have Medicaid'
                                                : ''}
                                        </div>
                                        <div>
                                            {localApplication?.ins_has_private
                                                ? 'I have Private insurance'
                                                : ''}
                                        </div>
                                        <div>
                                            {localApplication?.ins_has_cobra
                                                ? 'I have Private insurance through COBRA'
                                                : ''}
                                        </div>
                                        <div>
                                            {localApplication?.ins_has_none
                                                ? 'I do not have insurance'
                                                : ''}
                                        </div>
                                        <div>
                                            {localApplication?.ins_unknown
                                                ? "I don't know"
                                                : ''}
                                        </div>
                                    </div>
                                </div>
                                <div className='mb-[16px]'>
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Comments About Insurance
                                    </label>
                                    <div>{localApplication?.ins_comments}</div>
                                </div>
                                <div
                                    className={`mb-[16px] ${
                                        localApplication?.ins_company
                                            ? ''
                                            : 'bg-[#FDEAEB] px-1'
                                    }`}
                                >
                                    <label className='text-[12px]/[16px] text-[#949599]'>
                                        Insurance Company
                                        <span className='align-top text-[#F02D3A]'>
                                            *
                                        </span>
                                    </label>
                                    {localApplication?.ins_company ? (
                                        <div>
                                            {localApplication?.ins_company}
                                        </div>
                                    ) : (
                                        <div className='text-danger'>
                                            Required
                                        </div>
                                    )}
                                    {localApplication?.ins_premium ? (
                                        <div>
                                            Copay: $
                                            {localApplication?.ins_premium}
                                        </div>
                                    ) : (
                                        <div>
                                            I do not have a Premium or Copay
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <InsuranceInfoDrawer
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                application={localApplication}
                applicationId={application?.id}
                onUpdate={(response) => {
                    console.log('Insurance info updated:', response);
                    // The response includes complete campaign data, update context directly
                    const {
                        campaign: updatedCampaign,
                        campaign_id,
                        patient: updatedPatient,
                        csc: updatedCsc,
                        campaign_request: updatedCampaignRequest,
                        notes: updatedNotes,
                        coordinator: updatedCoordinator,
                        campaign_approval: updatedCampaignApproval,
                        financial_coordinator: updatedFinancialCoordinator,
                        ...applicationFields
                    } = response;

                    // Add campaign ID back to application object
                    const updatedApplication = {
                        ...applicationFields,
                        campaign: campaign_id,
                    };

                    // Update the context with the new data
                    campaignDispatch({
                        type: 'set_campaign',
                        payload: {
                            campaign: updatedCampaign,
                            patient: updatedPatient,
                            csc: updatedCsc,
                            campaign_request: updatedCampaignRequest,
                            notes: updatedNotes,
                            campaignPhotoInfo:
                                campaignData?.campaignPhotoInfo || null,
                            coordinator: updatedCoordinator,
                            financial_coordinator:
                                updatedFinancialCoordinator ||
                                campaignData?.financial_coordinator ||
                                null,
                            count: campaignData?.count || 0,
                            application: updatedApplication,
                            campaign_approval: updatedCampaignApproval,
                        },
                    });

                    // Also update local state for immediate UI feedback
                    setLocalApplication(updatedApplication);

                    // Refresh completion status
                    if (onDataSaved) {
                        onDataSaved();
                    }
                }}
            />
        </>
    );
};

export default InsuranceInfoTab;
